/**
 * SEC filings & events panel (v1.5.0): lists a company's recent disclosures
 * (10-K/10-Q/8-K/Form 4) pulled from SEC EDGAR during data sync and stored as
 * news items. Each links straight to the filing on SEC.gov. Renders nothing
 * until there's something to show, so it stays quiet for un-synced companies.
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { NewsItem } from "@/types";

export default function FilingsPanel({ companyId }: { companyId: string }): React.ReactElement | null {
  const [items, setItems] = useState<NewsItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<NewsItem[]>(`/companies/${companyId}/news`)
      .then(({ data }) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (items === null) return null;
  const filings = items.filter((item) => item.category === "sec_filing");
  if (filings.length === 0) return null;

  return (
    <div className="panel p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-mist-100">SEC filings &amp; events</h2>
        <span className="text-xs text-mist-500">Source: SEC EDGAR</span>
      </div>
      <p className="mt-1 text-sm text-mist-400">Recent disclosures filed with the SEC.</p>
      <ul className="mt-4 space-y-2">
        {filings.map((filing) => (
          <li
            key={filing.id}
            className="flex items-center justify-between gap-3 border-b border-ink-700 pb-2 text-sm last:border-0 last:pb-0"
          >
            <span className="text-mist-200">{filing.headline}</span>
            <span className="flex shrink-0 items-center gap-3">
              <span className="font-mono text-xs text-mist-400">
                {new Date(filing.published_at).toLocaleDateString()}
              </span>
              {filing.url && (
                <a
                  href={filing.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-signal-teal hover:underline"
                >
                  View →
                </a>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
